<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import SharedEntityDataTable from "src/components/shared/SharedEntityDataTable.vue";
import SharedFullPageLoader from "src/components/shared/SharedFullPageLoader.vue";
import type { DataTableFilterType } from "src/types/data-table";
import type { EntitySchema } from "src/types/entity-schema";
import { useLocalStorage } from "@vueuse/core";
import type { CrudRouteMeta } from "src/router/types";
import { useUnitService, type SyncUnitsResult } from "src/services/unit-service";
import { useNotification } from "src/composables/useNotification";

const route = useRoute();
const routeMeta = route.meta as CrudRouteMeta;
const routeName = computed(() => route.name?.toString());
const entitySchema: EntitySchema = {
  entity: routeName.value ?? "",
  ...routeMeta,
};

const { synchroniseUnitsInBackend } = useUnitService();
const { showSuccess, showError } = useNotification();
const isSyncing = ref(false);

const filterType = ref<DataTableFilterType>("simple");

const columnOptions = computed(() => {
  if (routeMeta.tableColumnsDefault) {
    return routeMeta.tableColumnsDefault
      .filter((col) => col.name !== "actions")
      .map((col) => ({
        header: col.name,
        field: col.name,
      }));
  }
  return Object.keys(routeMeta.tableColumns)
    .filter((column) => column !== "actions")
    .map((column) => ({
      header: column,
      field: column,
    }));
});

const selectedColumns = useLocalStorage<{ header: string; field: string }[]>(
  `crud-${routeName.value}-selected-columns`,
  [...columnOptions.value]
);

const filteredTableColumnsDefault = computed(() => {
  return selectedColumns.value.map((col) => ({ name: col.field }));
});

function onToggleSelectedColumns(columns: { header: string; field: string }[]) {
  selectedColumns.value = columns;
}

function formatSyncResult(result: SyncUnitsResult): string {
  return `Created: ${result.statistics.created}, Updated: ${result.statistics.updated}, Skipped: ${result.statistics.skipped}`;
}

async function onClickSync() {
  isSyncing.value = true;
  try {
    const result = await synchroniseUnitsInBackend();
    if(result.success) {
      showSuccess({ message: formatSyncResult(result), duration: 5000 });
    } else {
      showError({ message: "Failed to synchronise units." });
    }
  } catch {
    showError({ message: "Failed to synchronise units." });
  } finally {
    isSyncing.value = false;
  }
}
</script>

<template>
  <SharedFullPageLoader :visible="isSyncing" text="Syncing units..." />

  <SharedEntityDataTable
    v-model:filter-type="filterType"
    :schema="entitySchema"
    :filters="routeMeta.tableFilters"
    :table-columns="routeMeta.tableColumns"
    :table-columns-default="filteredTableColumnsDefault"
  >
    <template #header-left>
      <MultiSelect
        :modelValue="selectedColumns"
        :options="columnOptions"
        optionLabel="header"
        @update:modelValue="onToggleSelectedColumns"
        placeholder="Select Columns"
        :max-selected-labels="3"
      />
    </template>
    <template #header-right>
      <Button
        type="button"
        label="Sync units"
        icon="pi pi-sync"
        size="small"
        :loading="isSyncing"
        @click="onClickSync"
      />
    </template>
  </SharedEntityDataTable>
</template>
